/**
 * Phase 8 · Addendum F §33.1 — Sourcing canvas page.
 *
 * Renders the persistent History column (left) next to the existing
 * `MatchCanvasLayout` (right). The canvas is unchanged — we reuse the
 * same three-column Wrangle surface Smart Match uses, because:
 *
 *   1. Every affordance the Sourcing UX asks for (chat column, record
 *      column, right rail, stage-based transitions, clarification
 *      paraphrase) is already shipped inside `MatchCanvasLayout`.
 *   2. Duplicating the layout would fork state ownership — thread
 *      stage, shortlist, collection — none of which differ for
 *      Sourcing.
 *
 * Graceful recovery: if a user hits `/sourcing/:id` without the
 * sessionStorage intake (e.g. opened in a new tab, cleared storage,
 * or navigated via history column), we reconstitute the intake from
 * the thread row rather than redirecting the user out.
 */

import { useCallback, useEffect, useMemo } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { MatchCanvasLayout } from '@modules/smart-match/components/MatchCanvasLayout';
import {
  readPipelineIntake,
  writePipelineIntake,
  type PipelineIntake,
} from '@modules/smart-match/lib/matchCanvasPipeline';
import { getSourcingThread } from './lib/sourcingThreads';
import { SourcingHistoryColumn } from './components/SourcingHistoryColumn';

export function SourcingCanvasPage() {
  const { threadId = '' } = useParams<{ threadId: string }>();
  const navigate = useNavigate();

  const onNewChat = useCallback(() => {
    navigate('/sourcing/new');
  }, [navigate]);

  const intake = useMemo<PipelineIntake | null>(() => {
    if (!threadId) return null;
    const stashed = readPipelineIntake(threadId);
    if (stashed) return stashed;
    // Fallback — rebuild the intake from the thread row so a deep-link
    // or a cross-tab navigation doesn't kick the user back to the empty
    // state. Writing it back keeps subsequent renders on the cheap path.
    const thread = getSourcingThread(threadId);
    if (!thread) return null;
    const rebuilt: PipelineIntake = {
      prompt: thread.prompt,
      source: thread.scopeId === 'external' ? 'external' : 'pool',
      createdAt: thread.createdAt,
    };
    writePipelineIntake(threadId, rebuilt);
    return rebuilt;
  }, [threadId]);

  useEffect(() => {
    if (!threadId || !intake) return;
    document.title = `Sourcing · ${intake.prompt.slice(0, 60)}`;
  }, [threadId, intake]);

  if (!threadId) return <Navigate to="/sourcing/new" replace />;
  if (!intake) return <Navigate to="/sourcing/new" replace />;

  // Wrangle parity (§33, image 2). We no longer render History as a
  // sibling column — it stacks above the agent thread via `agentLeadingSlot`
  // so the left column reads: History → Source header → Copilot card →
  // chat turns → composer (matches the Wrangle reference exactly).
  return (
    <div className="-mx-6 -my-4 flex min-h-0 flex-1 flex-col">
      {/* §33.5 — Sourcing routes its chat through the C-41 streaming
          runner so `detectSourcingIntent` fans out to every configured
          connector (+ the always-on internal pool) and the candidate
          cards render via `AgentSourcingResults`. Smart Match stays on
          the legacy T-06 pipeline until we migrate it separately. */}
      <MatchCanvasLayout
        pipelineId={threadId}
        intake={intake}
        agentRunnerVariant="streaming"
        onNewChat={onNewChat}
        agentLeadingSlot={
          <SourcingHistoryColumn activeThreadId={threadId} variant="embedded" />
        }
      />
    </div>
  );
}
